import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import {
  full_and_need_to_refer_out,
  psypact_provider,
  treatment_program,
} from "../../constants/common";
import { useUserProfile } from "../../contexts/UserProfile";
import { ReactComponent as UserCheckIcon } from "../../assets/Icons/user-check.svg";
import { ReactComponent as GroupIcon } from "../../assets/Icons/no-poverty.svg";
import { ReactComponent as PhoneIcon } from "../../assets/Icons/phone.svg";
import { ReactComponent as GlobeIcon } from "../../assets/Icons/globe.svg";
import { ReactComponent as InboxIcon } from "../../assets/Icons/inbox.svg";
import { ReactComponent as LocationIcon } from "../../assets/Icons/location.svg";
import { ReactComponent as FacebookIcon } from "../../assets/Icons/facebook.svg";
import { ReactComponent as LinkIcon } from "../../assets/Icons/link.svg";
// import { ReactComponent as TwitterIcon } from '../../assets/Icons/twitter.svg';
import { ReactComponent as LinkedinIcon } from "../../assets/Icons/linkedin.svg";
import { ReactComponent as CoupleSmallIcon } from "../../assets/Icons/couple-small.svg";
import { ReactComponent as SessionTypesIcon } from "../../assets/Icons/session-types.svg";
import { ReactComponent as InPersonIcon } from "../../assets/Icons/building.svg";
import { formatPhoneNumber } from "../../utils/helper";
import { useSearchParams } from "react-router-dom";
import { session_type } from "../../constants/lists";
import { trackClickEvent } from "../../mixpanel";

const InfoItem = ({
  icon: Icon,
  label = "",
  value,
  isLink = false,
  onClick = null,
}) => {
  // Split the value by commas
  const items = value ? value.split(",").map((item) => item.trim()) : [];

  return (
    <>
      <Divider />
      <Box
        sx={{
          py: 1.5,
          px: 3,
          display: "flex",
          alignItems: "start",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 22,
            height: 26,
            display: "flex",
            alignItems: "start",
            color: "text.secondary",
          }}
        >
          <Icon />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {label && (
            <Typography component="span" variant="body1" fontWeight={600}>
              {label}:{" "}
            </Typography>
          )}

          {isLink ? (
            <Link
              href={value.startsWith("http") ? value : `https://${value}`}
              target="_blank"
              style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}
            >
              {items.map((item, index) => (
                <Typography
                  key={index}
                  component="span"
                  variant="body1"
                  sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                >
                  {index < items.length - 1 ? `${item},` : item}
                </Typography>
              ))}
            </Link>
          ) : (
            <>
              {items.map((item, index) => (
                <Typography
                  key={index}
                  component="span"
                  variant="body1"
                  sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                >
                  {index < items.length - 1 ? `${item},` : item}
                </Typography>
              ))}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

const SideComponents = () => {
  const { data } = useUserProfile();
  const [searchParams] = useSearchParams();
  const account_type = searchParams.get("account_type");

  const availabilityStatus =
    data.send_ref && data.user_referral
      ? "Accepting New Clients"
      : "Fully Booked";

  // Parse affiliated_program_links if it's a string
  const affiliatedLinks = React.useMemo(() => {
    if (!data?.info?.affiliated_program_links) return [];
    if (Array.isArray(data.info.affiliated_program_links)) {
      return data.info.affiliated_program_links;
    }
    try {
      // Fix unquoted property names in JSON string
      const fixedJson = data.info.affiliated_program_links.replace(
        /([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g,
        '$1"$2":',
      );
      return JSON.parse(fixedJson);
    } catch {
      return [];
    }
  }, [data?.info?.affiliated_program_links]);

  const subProgramLinks = React.useMemo(() => {
    if (!data?.info?.sub_program_links) return [];
    if (Array.isArray(data.info.sub_program_links)) {
      return data.info.sub_program_links;
    }
    try {
      // Fix unquoted property names in JSON string
      const fixedJson = data.info.sub_program_links.replace(
        /([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g,
        '$1"$2":',
      );
      return JSON.parse(fixedJson);
    } catch {
      return [];
    }
  }, [data?.info?.sub_program_links]);

  const address = data?.info?.address;


  return (
    <>
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ py: 1.5, px: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Availability
          </Typography>
        </Box>
        <Divider />
        <Box
          sx={{
            py: 1.5,
            px: 3,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 26,
              display: "flex",
              alignItems: "center",
              color: "text.secondary",
            }}
          >
            <UserCheckIcon />
          </Box>
          <Typography
            variant="body1"
            color={
              data.send_ref && data.user_referral
                ? "success.main"
                : "error.main"
            }
            textTransform="capitalize"
          >
            {availabilityStatus}
          </Typography>
        </Box>
        {data.send_ref && data.user_referral ? (
          <>
            {data.currently_served?.length > 0 && (
              <InfoItem
                icon={GroupIcon}
                value={data.currently_served
                  .map((item) => item?.name)
                  .join(", ")}
              />
            )}
            {data.info?.age_served_currently && (
              <InfoItem
                icon={CoupleSmallIcon}
                value={data.info.age_served_currently
                  .split(",")
                  .filter(Boolean)
                  .join(", ")}
              />
            )}
          </>
        ) : null}
        {data?.info?.session_types_currently && (
          <>
            {session_type
              .filter((element) => {
                if (element.value !== psypact_provider.value) {
                  return data?.info?.session_types_currently?.[element.value];
                }
              })
              ?.map((item, index) => {
                const isVideoTeletherapy = item.key === "Video/Teletherapy";
                const isInPerson = item.key === "In-person";
                return (
                  <InfoItem
                    key={index}
                    icon={
                      isVideoTeletherapy
                        ? SessionTypesIcon
                        : isInPerson
                          ? InPersonIcon
                          : SessionTypesIcon
                    }
                    value={item.key}
                  />
                );
              })}
          </>
        )}
      </Paper>

      {subProgramLinks?.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ py: 1.5, px: 3 }}>
            <Typography variant="h5" fontWeight={600}>
              Programs
            </Typography>
          </Box>
          <Divider />
          {subProgramLinks?.map((element, index) => (
            <React.Fragment key={index}>
              <Divider />
              <Box
                sx={{
                  py: 1.5,
                  px: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 26,
                    display: "flex",
                    alignItems: "center",
                    color: "border.dark",
                  }}
                >
                  <LinkIcon />
                </Box>
                <Link
                  href={
                    element.link.startsWith("http")
                      ? element.link
                      : `https://${element.link}`
                  }
                  target="_blank"
                  onClick={() => {
                    trackClickEvent("Clicked Related Programs Link", {
                      page: "View Profile",
                      component: "Side Component",
                    });
                  }}
                >
                  {element.name}
                </Link>
              </Box>
            </React.Fragment>
          ))}
        </Paper>
      )}
      {affiliatedLinks?.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ py: 1.5, px: 3 }}>
            <Typography variant="h5" fontWeight={600}>
              Affiliated Programs
            </Typography>
          </Box>
          <Divider />
          {affiliatedLinks?.map((element, index) => (
            <React.Fragment key={index}>
              <Divider />
              <Box
                sx={{
                  py: 1.5,
                  px: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 26,
                    display: "flex",
                    alignItems: "center",
                    color: "border.dark",
                  }}
                >
                  <LinkIcon />
                </Box>
                <Link
                  href={
                    element.link.startsWith("http")
                      ? element.link
                      : `https://${element.link}`
                  }
                  target="_blank"
                  onClick={() => {
                    trackClickEvent("Clicked Affiliation Link", {
                      page: "View Profile",
                      component: "Side Component",
                    });
                  }}
                >
                  {element.name}
                </Link>
              </Box>
            </React.Fragment>
          ))}
        </Paper>
      )}
      {(data?.info?.address?.city || data?.info?.address?.address1) && (
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ py: 1.5, px: 3 }}>
            <Typography variant="h5" fontWeight={600}>
              Address
            </Typography>
          </Box>
          <Divider />
          <Box
            sx={{
              py: 1.5,
              px: 3,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 26,
                display: "flex",
                alignItems: "center",
                color: "border.dark",
              }}
            >
              <LocationIcon />
            </Box>
            <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
              {address?.address1?.replace(/\\n/g, "\n")}
              {"\n"}
              {[address?.city, address?.state?.abbreviation, address?.zip_code]
                .filter(Boolean)
                .join(", ")}
            </Typography>
          </Box>
        </Paper>
      )}

      {(data?.info?.phone_number || data?.email) && (
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ py: 1.5, px: 3 }}>
            <Typography variant="h5" fontWeight={600}>
              Contact Information
            </Typography>
          </Box>
          {data?.info?.phone_number && (
            <InfoItem
              icon={PhoneIcon}
              label="Phone"
              value={formatPhoneNumber(data.info.phone_number)}
            />
          )}
          {data?.email && (
            <InfoItem
              icon={InboxIcon}
              label="Email"
              value={data.email}
              onClick={() => {
                window.location.href = `mailto:${data.email}?subject=&body=Hi ${data.first_name} ${data.last_name}`;
              }}
            />
          )}
        </Paper>
      )}

      {(data?.info?.office_number ||
        data?.info?.fax_number ||
        data?.info?.business_number ||
        data?.info?.website_link) && (
          <Paper sx={{ mb: 2 }}>
            <Box sx={{ py: 1.5, px: 3 }}>
              <Typography variant="h5" fontWeight={600}>
                Office Information
              </Typography>
            </Box>
            {data?.info?.office_number && (
              <InfoItem
                icon={PhoneIcon}
                label="Office"
                value={formatPhoneNumber(data.info.office_number)}
              />
            )}
            {account_type === treatment_program
              ? data?.info?.business_number && (
                <InfoItem
                  icon={InboxIcon}
                  label="Business Number"
                  value={formatPhoneNumber(data.info.business_number)}
                />
              )
              : data?.info?.fax_number && (
                <InfoItem
                  icon={InboxIcon}
                  label="Fax"
                  value={formatPhoneNumber(data.info.fax_number)}
                />
              )}
            {data?.info?.website_link && (
              <InfoItem
                icon={GlobeIcon}
                label="Website"
                value={data.info.website_link.toLowerCase()}
                isLink={true}
                target="_blank"
                onClick={() => {
                  trackClickEvent("Clicked Social Link - Website", {
                    page: "View Profile",
                    component: "Side Component",
                  });
                }}
              />
            )}
          </Paper>
        )}

      {(data?.info?.facebook || data?.info?.linkedin) && (
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ py: 1.5, px: 3 }}>
            <Typography variant="h5" fontWeight={600}>
              Social Media
            </Typography>
          </Box>
          {data?.info?.facebook && (
            <InfoItem
              icon={FacebookIcon}
              label="Facebook"
              value={data.info.facebook.toLowerCase()}
              isLink={true}
              target="_blank"
              onClick={() => {
                trackClickEvent("Clicked Social Link - Facebook", {
                  page: "View Profile",
                  component: "Side Component",
                });
              }}
            />
          )}
          {/* {data?.info?.twitter && (
            <InfoItem
              icon={TwitterIcon}
              label="Twitter"
              value={data.info.twitter.toLowerCase()}
              isLink={true}
              target='_blank'
            />
          )} */}
          {data?.info?.linkedin && (
            <InfoItem
              icon={LinkedinIcon}
              label="LinkedIn"
              value={data.info.linkedin.toLowerCase()}
              isLink={true}
              target="_blank"
              onClick={() => {
                trackClickEvent("Clicked Social Link - Linkedin", {
                  page: "View Profile",
                  component: "Side Component",
                });
              }}
            />
          )}
        </Paper>
      )}
    </>
  );
};

export default SideComponents;
